//import THREE from "three";
import MeshCustomPBRMaterial from "./material-custom-pbr";
import MeshNestedMaterial from "./mesh-nested-material";
import AssetLibrary from "../assets/asset-library";
import loadMaterial from "../assets/deserialize-material";
import { isSeamless } from "./cubemap-seams/detect";

const MaterialBuilder = new class MaterialBuilder {
    /**
     * 
     * @param {THREE.MeshStandardMaterialParameters} params 
     */
    build(params = { doNotSetValues: true }) {
        let material = null;

        switch (params.materialType) {
            case "nested":
                material = new MeshNestedMaterial();
                break;
            case "basic":
                material = new THREE.MeshBasicMaterial();
                break;
            case "pbr":
            default:
                material = new MeshCustomPBRMaterial();
                break;
        }

        if (!params.doNotSetValues) this.setValuesFromParams(material, params);

        isSeamless.then(seamless => {
            material.cubemapRendersWithSeams = !seamless;
        });

        return material;
    }

    async fromData(uuid, data) {
        const envMap = AssetLibrary.envLight.activeMap;
        const envMapCubeRes = AssetLibrary.envLight.resolution;
        const { textures } = AssetLibrary;

        const { material } = await loadMaterial({ uuid, data, envMap, envMapCubeRes, textures });

        return material;
    }

    /**
     * 
     * @param {THREE.MeshStandardMaterial} material 
     * @param {*} params 
     */
    setValuesFromParams(material, params) {
        const supportsEnvMap = params.materialType !== "basic";

        if (supportsEnvMap) {
            material.envMap = AssetLibrary.envLight.activeMap;

            if (params.materialType === 'pbr') {
                material.envMapCubeRes = AssetLibrary.envLight.resolution;
            }
        }

        if (params.map !== undefined) material.map = params.map;
        if (params.morphTargets !== undefined) material.morphTargets = params.morphTargets;
        if (params.morphNormals !== undefined) material.morphNormals = params.morphNormals;
        if (params.skinning !== undefined) material.skinning = params.skinning;

        if (params.uuid !== undefined) material.uuid = params.materialUuid;
        if (params.color !== undefined) material.color.setHex(parseInt(params.color));
        if (params.secondaryColor) material.secondaryColor.setHex(parseInt(params.secondaryColor));
        if (params.siding !== undefined) {
            switch (params.siding) {
                case "Front":
                    material.side = THREE.FrontSide;
                    break;
                case "Back":
                    material.side = THREE.BackSide;
                    break;
                case "Both":
                    material.side = THREE.DoubleSide;
                    break;
                default:
                    throw new Error("Who done did it?");
            }
        }
        if (params.side !== undefined) material.side = params.side;

        [
            "fog",
            "roughness",
            "metalness",
            "transparent",
            "opacity",
            "alphaTest",
            "depthWrite",
            "depthTest",
            "wireframe",
            "bumpScale",
            "flatShading",
            "emissiveIntensity",
            "envMapIntensity"
        ].forEach(key => {
            if (params[key] !== undefined)
                material[key] = params[key];
        });

        if (params.normalScale && "normalScale" in material) material.normalScale.set(params.normalScale, params.normalScale);
        if (params.emissive && "emissive" in material) material.emissive.setHex(parseInt(params.emissive));

        switch (params.materialType) {
            case "nested":
                this._setNestedSpecific(material, params);
                break;
            case "basic":
                this._setBasicSpecific(material, params);
                break;
            case "pbr":
            default:
                this._setPBRSpecific(material, params);
                break;
        }
    }

    /**
     * 
     * @param {THREE.MeshStandardMaterial} material 
     * @param {*} params 
     */
    _setBasicSpecific(material, params) {
        if (params.mapUuid) {
            const texture = AssetLibrary.textures[params.mapUuid];
            material.map = texture;
        }

        if (params.aoMapUuid) {
            const texture = AssetLibrary.textures[params.aoMapUuid];
            material.aoMap = texture;
        }

        if (params.alphaMapUuid) {
            const texture = AssetLibrary.textures[params.alphaMapUuid];
            material.alphaMap = texture;
        }
    }

    /**
     * 
     * @param {THREE.MeshStandardMaterial} material 
     * @param {*} params 
     */
    _setPBRSpecific(material, params) {
        if (params.mapUuid) {
            const texture = AssetLibrary.textures[params.mapUuid];
            material.map = texture;
        }

        if (params.roughnessMapUuid) {
            const texture = AssetLibrary.textures[params.roughnessMapUuid];
            material.roughnessMap = texture;
        }

        if (params.metalnessMapUuid) {
            const texture = AssetLibrary.textures[params.metalnessMapUuid];
            material.metalnessMap = texture;
        }

        if (params.emissiveMapUuid) {
            const texture = AssetLibrary.textures[params.emissiveMapUuid];
            material.emissiveMap = texture;
        }

        if (params.bumpMapUuid) {
            const texture = AssetLibrary.textures[params.bumpMapUuid];
            material.bumpMap = texture;
        }

        if (params.normalMapUuid) {
            const texture = AssetLibrary.textures[params.normalMapUuid];
            material.normalMap = texture;
        }

        if (params.aoMapUuid) {
            const texture = AssetLibrary.textures[params.aoMapUuid];
            material.aoMap = texture;
        }

        if (params.alphaMapUuid) {
            const texture = AssetLibrary.textures[params.alphaMapUuid];
            material.alphaMap = texture;
        }

        if (params.reliefMode !== undefined) {
            switch (params.reliefMode) {
                case "Bump":
                    material.normalMap = null;
                    break;
                case "Normal":
                    material.bumpMap = null;
                    break;
            }
        }

        if (params.smoothClipEnd !== undefined) material.smoothClipEnd = params.smoothClipEnd;
        if (params.smoothClipStart !== undefined) material.smoothClipStart = params.smoothClipStart;
        if (params.smoothClipEnabled !== undefined) material.smoothClipEnabled = params.smoothClipEnabled;
        if (params.bumpScale2 !== undefined) material.bumpScale2 = params.bumpScale2;
    }

    /**
     * 
     * @param {THREE.MeshStandardMaterial} material 
     * @param {*} params 
     */
    _setNestedSpecific(material, params) {
        if (params.layers) {
            if (params.layers) {
                params.layers.forEach(l => {
                    if (l.distance instanceof Array) {
                        l.distance = new THREE.Vector2().fromArray(l.distance);
                    };
                })

            }

            material.setLayers(params.layers);
        }

        if (params.mapUuid) {
            const texture = AssetLibrary.textures[params.mapUuid];
            material.layers[0].map = texture;
        }

        if (params.bumpMapUuid) {
            const texture = AssetLibrary.textures[params.bumpMapUuid];
            material.layers[0].normalMap = texture;
        }

        if (params.normalMapUuid) {
            const texture = AssetLibrary.textures[params.normalMapUuid];
            material.layers[0].normalMap = texture;
        }
    }

    buildAndReplaceMaterial(uuid, params) {
        const material = this.build(params);
        material.uuid = uuid;

        AssetLibrary.materials[uuid] = material;

        Object.keys(Q3.data.assets.objects).forEach(key => {
            const materialUuids = Q3.data.assets.objects[key].material;
            if (materialUuids) {
                let object = AssetLibrary.objects[key];

                if (materialUuids instanceof Array) {
                    for (let [i, materialUuid] of materialUuids.entries()) {
                        if (materialUuid === uuid) {
                            object.material[i] = material;
                        }
                    }
                } else {
                    if (materialUuids === uuid) {
                        object.material = material;
                    }
                }
            }
        });
    }
}

export default MaterialBuilder;
export { MaterialBuilder };